import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { fetchPromoStoreUserPoints } from "../../../../../services/promoStoreAddons";
const errorMessage = (error) => error?.userMessage || error?.response?.data?.message || "Could not read points from WordPress.";

export function usePromoStoreUserPoints({ storeId, instanceUniqueId, isActive, users, refreshKey }) {
  const scope = `${instanceUniqueId}:${storeId}`;
  const ids = [...new Set(users.map((user) => Number(user.wpUserId)).filter((id) => Number.isSafeInteger(id) && id > 0))].sort((a, b) => a - b).join(",");
  const [state, setState] = useState({});
  useEffect(() => {
    let cancelled = false;
    setState((previous) => ({ ...(previous.scope === scope ? previous : {}), scope, loading: true, balances: {}, error: "" }));
    if (!isActive || !storeId || !instanceUniqueId) { setState({}); return undefined; }
    const load = async () => {
      const allIds = ids ? ids.split(",") : [];
      const balances = {};
      let latest, label;
      for (let index = 0; index < Math.max(allIds.length, 1); index += 100) {
        latest = await fetchPromoStoreUserPoints(storeId, instanceUniqueId, allIds.length ? { userIds: allIds.slice(index, index + 100).join(",") } : {});
        if (cancelled) return;
        if (!latest.enabled) break;
        if (label !== undefined && label !== latest.label) throw Object.assign(new Error("Points settings changed"), { userMessage: "Points settings changed during loading. Refresh Points balances." });
        label = latest.label;
        Object.assign(balances, latest.balances);
        // Preserve the enabled columns during subsequent batch loading.
        setState({ scope, enabled: latest.enabled, label: latest.label, balances: {}, loading: true, error: "" });
      }
      if (!cancelled) setState({ scope, enabled: latest.enabled, label: latest.label, balances: latest.enabled ? balances : {}, loading: false, error: "" });
    };
    load().catch((error) => { if (!cancelled) setState((previous) => ({ ...previous, scope, loading: false, balances: {}, error: errorMessage(error) })); });
    return () => { cancelled = true; };
  }, [scope, storeId, instanceUniqueId, isActive, ids, refreshKey]);
  return state.scope === scope && isActive ? state : {};
}

const types = { starting: "Starting grant", spending: "Spending", reversal: "Reversal", reservation: "Reservation / release", adjustment: "Adjustment" };
export default function PromoStorePointsHistory({ storeId, instanceUniqueId, userId, enabled, refreshKey }) {
  const scope = `${instanceUniqueId}:${storeId}:${userId}`;
  const [page, setPage] = useState(1), [revision, setRevision] = useState(0), [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false), [error, setError] = useState("");
  useEffect(() => { setPage(1); setResult(null); }, [scope]);
  useEffect(() => {
    let cancelled = false;
    setError("");
    if (!enabled || !userId) { setResult(null); setLoading(false); return undefined; }
    setLoading(true);
    fetchPromoStoreUserPoints(storeId, instanceUniqueId, { userId, page, perPage: 25 })
      .then((data) => { if (!cancelled) setResult({ ...data, scope }); })
      .catch((failure) => { if (!cancelled) { setResult(null); setError(errorMessage(failure)); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [scope, storeId, instanceUniqueId, userId, enabled, page, revision, refreshKey]);
  if (!enabled) return null;
  const current = result?.scope === scope ? result : null;
  const balance = current?.balances?.[userId];
  return <Box>
    <Stack direction="row" justifyContent="space-between" alignItems="center"><Typography variant="subtitle2">Points history</Typography><Button disabled={loading} onClick={() => setRevision((value) => value + 1)}>Refresh points history</Button></Stack>
    {loading && <Typography role="status">Loading points history…</Typography>}
    {error && <Alert severity="error">{error}</Alert>}
    {current && !current.enabled && <Typography>The Points add-on is disabled.</Typography>}
    {current?.enabled && <>
      <Typography sx={{ mb: 1 }}>Current {current.label} balance: {balance ? balance.balance.toLocaleString() : "Unavailable"}</Typography>
      {balance?.source === "legacy" && <Alert severity="info" sx={{ mb: 1 }}>Using the shared legacy balance, matching WordPress’s fallback. No balance was migrated or repaired.</Alert>}
      {!current.historyAvailable ? <Alert severity="info">Points history is unavailable because this store has no ledger table.</Alert> : <>
        <Typography variant="caption" color="text.secondary">Recorded ledger entries; they may not include every historical balance change. Dates use the store’s local time.</Typography>
        <TableContainer><Table size="small" aria-label="User points history">
          <TableHead><TableRow>{["Date", "Type", "Points", "Recorded balance", "Order", "Reason"].map((label) => <TableCell key={label}>{label}</TableCell>)}</TableRow></TableHead>
          <TableBody>{current.transactions.map((row) => <TableRow key={row.id}>
            <TableCell>{row.createdAt}{row.legacy && <Typography variant="caption" display="block">Legacy site entry</Typography>}</TableCell><TableCell>{types[row.type]}</TableCell>
            <TableCell>{row.delta > 0 ? "+" : ""}{row.delta.toLocaleString()}</TableCell><TableCell>{row.balanceAfter.toLocaleString()}</TableCell><TableCell>{row.orderId ? `#${row.orderId}` : "—"}</TableCell><TableCell>{row.reason}</TableCell>
          </TableRow>)}</TableBody>
        </Table></TableContainer>
        {!current.transactions.length && <Typography>No recorded points transactions.</Typography>}
        <Stack direction="row" spacing={1} alignItems="center"><Button disabled={loading || page === 1} onClick={() => setPage((value) => value - 1)}>Previous points page</Button><Typography variant="body2">Page {page} · {current.total} transactions</Typography><Button disabled={loading || page * 25 >= current.total} onClick={() => setPage((value) => value + 1)}>Next points page</Button></Stack>
      </>}
    </>}
  </Box>;
}
